import {Injectable} from "@angular/core";
import {IUserResultT} from "app/main/pages/contract-control/contract-control.permission.service";
import {AccessLevelEnum, AccessLevelPriority, IAccessType} from "app/main/pages/users/roles/roles.service";
import {UserSettingsFacade} from "app/store/user-settings.facade";
import {catchError, combineLatest, map, Observable, switchMap} from "rxjs";
import {GlobalService} from "../global.service";
import {CategoryShareModeEnum} from "../../main/pages/categories.dto";
import {UserSettings, Workspace} from "../user-settings.dto";
import {BasePermissionService} from "./base-permission-service";
import {PermissionsService} from "./permissions.service";
import { checkCategoryOverDocType } from "app/store/selectors/roles.selectors";

@Injectable({
    providedIn: 'root'
})


export class TemplatePermissionsService extends BasePermissionService {


    constructor(
        protected _permissionService: PermissionsService,
        protected _userSettingsFacade: UserSettingsFacade,
        protected _globalService: GlobalService
    ) {
        super(
            _permissionService, 
            _permissionService.isTenantOwner$,
            _globalService.getUserDefaultWorkSpace(),
            _userSettingsFacade._userSettings$
        );
     }




    /**
     * Retrieves the access level for a given template ID.
     *
     * @param id - The ID of the template for which to retrieve the access level.
     * @returns An Observable that emits the access level of the specified template.
     */
     protected getAccessLevel(id: number, docTypeId:number, categoryIds:number | number[]): Observable<AccessLevelEnum> {
        return this._permissionService.templateAccessLevel(id, docTypeId, categoryIds);
     }



    /**
     * Checks if the user has full access based on the provided access level.
     *
     * @param id - The ID of the user.
     * @param result - The result object containing user information.
     * @returns An Observable that emits a boolean indicating whether the user has full access.
     */
    hasFullAccess(id: number, docTypeId:number, categoryId:number, result: IUserResultT): Observable<boolean> {
        return this.verifyPermission(id, docTypeId, categoryId, result).pipe( 
            map(x => x === AccessLevelEnum.full_access)
        );
    }



    /**
     * Determines if the user has edit permissions for a given resource.
     *
     * @param id - The unique identifier of the resource.
     * @param result - The user result object containing user information.
     * @returns An Observable that emits a boolean indicating whether the user has edit permissions.
     */
    canEdit(id: number,docTypeId:number, categoryIds: number | number[], result: IUserResultT): Observable<boolean> {
        return this.verifyPermission(id, docTypeId, categoryIds,  result).pipe(
            map(x => [AccessLevelEnum.edit, AccessLevelEnum.use_and_edit, AccessLevelEnum.full_access].includes(x))
        );
    }

 
    /**
     * Checks and returns the appropriate access level based on the provided parameters.
     *
     * @param {boolean} hasFullAccess - Indicates if the user has full access.
     * @param {AccessLevelEnum} accessLevel - The current access level of the user.
     * 
     * @private
     * @returns {AccessLevelEnum} - Returns `AccessLevelEnum.full_access` if `hasFullAccess` is true,
     * otherwise returns the provided `accessLevel`. If `accessLevel` is not provided, returns `null`.
     */
    protected checkRolesAccessLevel(hasFullAccess: boolean, accessLevel: AccessLevelEnum): AccessLevelEnum {
        return hasFullAccess ? AccessLevelEnum.full_access : accessLevel ? accessLevel : null;
    }






    /**
     * Checks if the user has access to a specific template type within a workspace.
     *
     * @param userSettings$ - The settings of the user, including their ID and groups.
     * @param result - An object containing the user ID and workspace ID.
     * @param defaultWorkSpace$ - The default workspace of the user.
     * @param templateTypeId - The ID of the template type to check access for.
     * 
     * @private
     * @returns A AccessLevelEnum indicating whether the user has access to the template type, or null if no access.
     */
    protected checkGroupAccess(
        userSettings$: UserSettings,
        result: IUserResultT,
        defaultWorkSpace$: Workspace,
        templateTypeId: number,
        docTypeId: number,
        categoryIds: number | number[],
        parentCategoryIds: number[]
    ): AccessLevelEnum {
        const highestAccessLevel: AccessLevelEnum = null;
        if (userSettings$.id === result.user_id) {
            return AccessLevelEnum.full_access;
        }
        if ( !!result?.workspace_id && defaultWorkSpace$.id !== result.workspace_id) {
            return highestAccessLevel;
        }
       
        return this.getHighestAccessLevel(userSettings$, templateTypeId, docTypeId, categoryIds, parentCategoryIds);
    }




    /**
     * Determines the highest access level a user has for a specific template type.
     *
     * @param userSettings$ - The settings of the user, including their groups and access levels.
     * @param templateTypeId - The ID of the template type to check access for.
     * @returns The highest access level the user has for the specified template type.
     */
    private getHighestAccessLevel(userSettings$: UserSettings, templateTypeId: number, docTypeId: number, categoryIds: number | number[], parentCategoryIds: number[]): AccessLevelEnum {
        const accessLevelPriority = AccessLevelPriority;
    
        const appliedAccessLevels: AccessLevelEnum[] = [];
    
        for (const group of userSettings$.groups) {
            if (!group.template_types?.length) continue;
    
            // Check if the user is in the group
            const groupUser = group.group_users.find(x => x.user_id === userSettings$.id);
            if (!groupUser) continue; // Skip if user is not in this group
    
            // Check if the contract type is in the group
            const isTemplateTypesContains = group.template_types.some(x => x.document_type_id === templateTypeId && checkCategoryOverDocType(x, categoryIds, parentCategoryIds));
    
            // Validate access based on the required level
            if (
                [AccessLevelEnum.full_access, AccessLevelEnum.use_and_edit, AccessLevelEnum.use_only].includes(groupUser.templates_access_level) &&
                isTemplateTypesContains &&
                (groupUser)
            ) {
                appliedAccessLevels.push(groupUser.templates_access_level);
            }
        }
    
        // Determine the highest priority access level
        return appliedAccessLevels.sort((a, b) => accessLevelPriority.indexOf(a) - accessLevelPriority.indexOf(b))[0] || null;
    }
}
